import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

import { useHistory } from 'react-router-dom';

import Navbar from 'components/layout/NavbarNew';
import UserMenu from 'components/layout/UserMenu';
import Loading from 'components/layout/loading';

import Button from 'design-system/Button';

import { InstallBaseData } from 'services/basesServices';
import { useNewRefuel } from '../hooks/useNewRefuel';
import { STEP } from '../providers';
import { toLocationSummaries, toConfirmationLocations } from '../adapters';

import SelectBases from '../Steps/SelectBases';
import RefuelLocationForm from '../RefuelLocationForm';
import LocationReview from '../LocationReview';
import OrderGeneralSummary from '../OrderGeneralSummary';
import OrderConfirmation from '../OrderConfirmation';

import ModalError from './ModalError';
import ModalGauge from './ModalGauge';

import {
  Container,
  Header,
  Title,
  SubTitle,
  ContainerWrap,
  HeaderText,
  ModalStyled,
} from './styles';

const ScrollTopButton = ({
  onClick,
}: {
  onClick: () => void;
}): React.ReactElement =>
  ReactDOM.createPortal(
    <button
      type="button"
      aria-label="Voltar ao topo"
      onClick={onClick}
      style={{
        position: 'fixed',
        bottom: 170,
        right: 24,
        width: 48,
        height: 48,
        borderRadius: '50%',
        border: 'none',
        background: '#0B29FB',
        color: '#ffffff',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
        zIndex: 2147483000,
      }}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M12 19V5M6 11l6-6 6 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>,
    document.body
  );

const RefuelContainer: React.FC = () => {
  const history = useHistory();
  const {
    enabledStep,
    handleEnabledStep,
    handleSelectedBase,
    selectedBases,
    selectedBase,
    refuelData,
    responsibleByBase,
    statusResponse,
    confirmRefuels,
    finishRefuel,
    isLoading,
  } = useNewRefuel();

  const [showGauge, setShowGauge] = useState(false);
  const [errorCode, setErrorCode] = useState(0);
  const [editingFromSummary, setEditingFromSummary] = useState(false);
  const [attempted, setAttempted] = useState<Record<string, boolean>>({});
  const [fillView, setFillView] = useState<'form' | 'review'>('form');

  const scrollToTop = (): void => {
    const anchor = document.getElementById('refuel-form-top');
    if (anchor) {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const markAttempted = (baseName: string): void =>
    setAttempted((prev) => ({ ...prev, [baseName]: true }));

  const index = Math.max(
    0,
    selectedBases.findIndex((b) => b.name === selectedBase?.name)
  );
  const currentBase = selectedBases[index];
  const isLast = index === selectedBases.length - 1;

  const missingStep = (base: InstallBaseData): number => {
    if (!base.percentageValidate) return 2;
    if (!base.dateValidate) return 3;
    const responsible = responsibleByBase[base.name];
    if (
      !responsible ||
      !responsible.name ||
      !responsible.phone ||
      !responsible.email ||
      !responsible.details
    ) {
      return 4;
    }
    return 0;
  };

  const isBaseComplete = (base: InstallBaseData): boolean =>
    missingStep(base) === 0;

  const allBasesComplete =
    selectedBases.length > 0 && selectedBases.every(isBaseComplete);

  const handleStep1Next = (): void => {
    if (selectedBases.length === 0) {
      setErrorCode(1);
      return;
    }
    setEditingFromSummary(false);
    setFillView('form');
    handleEnabledStep(STEP.FILL);
  };

  // Avanço do FORMULÁRIO do local: valida e, se completo, mostra o "Resumo do
  // pedido" daquele local (review). Se veio do lápis do resumo geral, salva e
  // volta direto para o resumo geral.
  const handleAdvance = (): void => {
    if (!currentBase) return;
    const miss = missingStep(currentBase);
    if (miss > 0) {
      markAttempted(currentBase.name);
      setErrorCode(miss);
      return;
    }
    if (editingFromSummary) {
      setEditingFromSummary(false);
      handleEnabledStep(STEP.SUMMARY);
      return;
    }
    setFillView('review');
  };

  const handleEditLocation = (): void => {
    setFillView('form');
    scrollToTop();
  };

  const handleReviewNext = (): void => {
    if (!isLast) {
      handleSelectedBase(selectedBases[index + 1].name);
      setFillView('form');
      scrollToTop();
      return;
    }
    const incomplete = selectedBases.find((b) => !isBaseComplete(b));
    if (incomplete) {
      markAttempted(incomplete.name);
      setErrorCode(missingStep(incomplete));
      handleSelectedBase(incomplete.name);
      setFillView('form');
      return;
    }
    handleEnabledStep(STEP.SUMMARY);
  };

  const advanceLabel = editingFromSummary
    ? 'Salvar e voltar ao resumo'
    : 'Confirmar e concluir';

  const editFromSummary = (id: string): void => {
    setEditingFromSummary(true);
    setFillView('form');
    handleEnabledStep(STEP.FILL);
    handleSelectedBase(id);
  };

  const handleConfirm = async (): Promise<void> => {
    await confirmRefuels();
    handleEnabledStep(STEP.DONE);
  };

  const handleBackHome = (): void => {
    finishRefuel();
    history.push('/');
  };

  const goBackToStep1 = (): void => {
    setEditingFromSummary(false);
    handleEnabledStep(STEP.BASES);
  };

  const locations = useMemo(
    () => toLocationSummaries(selectedBases, refuelData, responsibleByBase),
    [selectedBases, refuelData, responsibleByBase]
  );

  const confirmations = useMemo(
    () => toConfirmationLocations(statusResponse),
    [statusResponse]
  );

  const renderStep = (): React.ReactNode => {
    switch (enabledStep) {
      case STEP.BASES:
        return <SelectBases />;
      case STEP.FILL: {
        if (!currentBase) return null;
        if (fillView === 'review') {
          const loc = locations.find((l) => l.id === currentBase.name);
          return loc ? (
            <LocationReview
              title={currentBase.nickname}
              counter={`Central de abastecimento ${index + 1} de ${
                selectedBases.length
              }`}
              items={loc.items}
              isLast={isLast}
              multi={selectedBases.length > 1}
              onEdit={handleEditLocation}
              onNext={handleReviewNext}
            />
          ) : null;
        }
        return (
          <RefuelLocationForm
            base={currentBase}
            index={index}
            total={selectedBases.length}
            advanceLabel={advanceLabel}
            onAdvance={handleAdvance}
            onGasHelp={() => setShowGauge(true)}
            showErrors={!!attempted[currentBase.name]}
          />
        );
      }
      case STEP.SUMMARY:
        return (
          <OrderGeneralSummary
            locations={locations}
            onEditLocation={editFromSummary}
            onConfirm={handleConfirm}
            confirmDisabled={!allBasesComplete}
          />
        );
      case STEP.DONE:
        return (
          <OrderConfirmation
            locations={confirmations}
            onBackHome={handleBackHome}
          />
        );
      default:
        return null;
    }
  };

  const showAlterarLocais =
    enabledStep === STEP.FILL || enabledStep === STEP.SUMMARY;

  const showSolicitar = enabledStep === STEP.BASES;

  return (
    <Navbar selected="pedido-abastecimento" openSubMenu="abastecimento">
      <UserMenu intro={() => true} />
      <Container>
        {isLoading && <Loading />}
        <Header>
          <HeaderText>
            <Title>Novo pedido</Title>
            <SubTitle>
              Siga as etapas abaixo para que possamos efetuar seu pedido
            </SubTitle>
          </HeaderText>
          {showSolicitar && (
            <Button
              variant="primary"
              shape="pill"
              size="xl"
              disabled={selectedBases.length === 0}
              onClick={handleStep1Next}
            >
              Solicitar abastecimento
            </Button>
          )}
          {showAlterarLocais && (
            <Button
              variant="secondary"
              shape="pill"
              size="xl"
              onClick={goBackToStep1}
            >
              Alterar central de abastecimento
            </Button>
          )}
        </Header>

        <ContainerWrap>{renderStep()}</ContainerWrap>

        {enabledStep === STEP.FILL && fillView === 'form' && (
          <ScrollTopButton onClick={scrollToTop} />
        )}

        <ModalStyled
          show={showGauge}
          onHide={() => setShowGauge(false)}
          animation={false}
          centered
        >
          <ModalGauge closeModal={() => setShowGauge(false)} />
        </ModalStyled>
        <ModalStyled
          show={errorCode > 0}
          onHide={() => setErrorCode(0)}
          animation={false}
          centered
        >
          <ModalError
            closeModal={() => setErrorCode(0)}
            stepError={errorCode}
          />
        </ModalStyled>
      </Container>
    </Navbar>
  );
};

export default RefuelContainer;
